import "./FilterPanelLevel1.css";
import { useCallback } from "react";
import { Typography, TextField, Button, Stack, Slider } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";

export type FiltersL1 = {
  sizeMB: [number | null, number | null];
};

export const FILTERS_L1_DEFAULTS: FiltersL1 = {
  sizeMB: [null, null],
};

type Props = {
  filters: Partial<FiltersL1>;
  setFilters: React.Dispatch<React.SetStateAction<FiltersL1>>;
};

const toNumOrNull = (v: string | number): number | null => {
  const s = String(v).trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

function Help({ id, tip, label }: { id: string; tip: string; label: string }) {
  return (
    <button
      type="button"
      className="fp-help"
      aria-label={`What is "${label}"?`}
      aria-describedby={id}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      ?
      <span id={id} role="tooltip" className="fp-help-tip">
        {tip}
      </span>
    </button>
  );
}

function TitleWithHelp({
  children,
  id,
  tip,
}: {
  children: React.ReactNode;
  id: string;
  tip: string;
}) {
  return (
    <div className="fp-rowtitle">
      <Typography gutterBottom className="fp-rowtitle-text">
        {children}
      </Typography>
      <Help id={id} tip={tip} label={String(children)} />
    </div>
  );
}

type SingleSliderKey = "sizeMB";

type SingleSliderProps = {
  label: string;
  tipId: string;
  tip: string;
  keyName: SingleSliderKey;
  value: number | null;
  onChange: (key: SingleSliderKey, v: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  unitSuffix?: string;
};

const SingleSlider: React.FC<SingleSliderProps> = ({
  label,
  tipId,
  tip,
  keyName,
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unitSuffix,
}) => {
  const numVal = typeof value === "number" ? clamp(value, min, max) : null;

  return (
    <>
      <TitleWithHelp id={tipId} tip={tip}>
        {label}
      </TitleWithHelp>

      <Stack direction="row" spacing={1} alignItems="center">
        <Slider
          value={numVal ?? min}
          min={min}
          max={max}
          step={step}
          valueLabelDisplay="auto"
          onChange={(_, v) => {
            const n = Array.isArray(v) ? v[0] : v;
            onChange(keyName, toNumOrNull(n) as number | null);
          }}
          sx={numVal === null ? { opacity: 0.4 } : undefined}
        />

        <TextField
          size="small"
          type="number"
          label="Value"
          placeholder="—"
          value={numVal ?? ""}
          onChange={(e) => {
            const n = toNumOrNull(e.target.value);
            if (n === null) onChange(keyName, null);
            else onChange(keyName, clamp(n, min, max));
          }}
          inputProps={{ min, max, step }}
          style={{ width: 110 }}
        />
        {unitSuffix ? (
          <span style={{ color: "#6b7280", fontSize: 12 }}>{unitSuffix}</span>
        ) : null}
      </Stack>
    </>
  );
};

const FilterPanelLevel1: React.FC<Props> = ({ filters, setFilters }) => {
  const f: FiltersL1 = { ...FILTERS_L1_DEFAULTS, ...(filters as FiltersL1) };

  const setExact = useCallback(
    (key: SingleSliderKey, v: number | null) => {
      setFilters((prev) => {
        const base: FiltersL1 = {
          ...FILTERS_L1_DEFAULTS,
          ...(prev as FiltersL1),
        };
        const next: [number | null, number | null] =
          v === null ? [null, null] : [v, v];
        return { ...base, [key]: next };
      });
    },
    [setFilters]
  );

  const clearAll = () => setFilters({ ...FILTERS_L1_DEFAULTS });

  return (
    <div className="filter-panel-l1">
      <div className="filter-inner-l1">
        <div className="filter-section-l1">
          <SingleSlider
            label="Size (MB)"
            tipId="fp-tip-size"
            tip="Total size of the image version (in MB)."
            keyName="sizeMB"
            value={f.sizeMB?.[0] ?? null}
            onChange={setExact}
            min={0}
            max={5000}
            step={1}
            unitSuffix="MB"
          />
        </div>

        <div className="filter-clear-row-l1">
          <Button
            variant="contained"
            color="error"
            onClick={clearAll}
            className="filter-clear-btn-l1"
            aria-label="Clear filters"
            title="Clear all filters"
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilterPanelLevel1;
